import React, {useState, useRef, useEffect, useMemo} from "react";
import {jwtDecode} from "jwt-decode";
import {useAppContext} from "./context/AppContext";
import ChatHistory from "./components/ChatHistory";
import ChatInput from "./components/ChatInput";
import Footer from "./components/Footer";
import SiderBar from "./components/SiderBar";
import {Message, JWTUserProps} from "./types/types";
import LoginFormContainer from "./components/LoginFormContainer";
import Spinner from "./components/Spinner";

const WEBSOCKET_URL = "wss://aespasol-testdomain.duckdns.org/chatbot/ws/chat/";

const App = () => {
  const user = useMemo(() => {
    const token = localStorage.getItem("iris-accessToken");
    if (!token) return null;
    try {
      return jwtDecode<JWTUserProps>(token);
    } catch (err) {
      console.error("Invalid token:", err);
      return null;
    }
  }, []);

  const {isLoggedIn} = useAppContext();

  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isThinking, setIsThinking] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!isLoggedIn) return;

    let reconnectTimeout: NodeJS.Timeout;
    let reconnectAttempts = 0;

    const connectSocket = () => {
      if (socketRef.current?.readyState === WebSocket.OPEN) return;

      const socket = new WebSocket(WEBSOCKET_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        setIsConnected(true);
        reconnectAttempts = 0;
      };

      socket.onclose = () => {
        setIsConnected(false);

        if (!document.hidden) {
          reconnectAttempts++;
          const delay = Math.min(5000, reconnectAttempts * 1000);
          reconnectTimeout = setTimeout(connectSocket, delay);
        }
      };

      socket.onerror = () => {
        socket.close();
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          if (data?.thinking) {
            setIsThinking(true);
            return;
          }

          const sender = data?.botResponse?.role;
          const rawMessage = data?.botResponse?.content[0]?.text || "";
          const imageObject: Record<string, string> =
            data?.images && typeof data.images === "object" ? data.images : {};

          const formattedMessage = rawMessage
            .replace(
              /<url=([^>]+)>(.*?)<\/url>/g,
              (_: any, url: any, text: any) =>
                `<a href="${url}" target="_blank" rel="noopener noreferrer">${text}</a>`
            )
            .replace(/\[([^\]]+)\]/g, (match: any, filename: any) => {
              const url = imageObject[filename];
              return url
                ? `<img src="${url}" alt="${filename}" style="max-width:100%; margin:10px 0; display:block;" />`
                : match;
            });

          const botMessage: Message = {
            sender,
            message: formattedMessage,
            imageArray: Object.values(imageObject),
          };

          setMessages((prev) => [...prev, botMessage]);
          setIsLoading(false);
          setIsThinking(false);
        } catch {
          setIsLoading(false);
          setIsThinking(false);
        }
      };
    };

    connectSocket();

    const handleBeforeUnload = () => {
      socketRef.current?.close();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      clearTimeout(reconnectTimeout);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      if (!isLoggedIn) {
        socketRef.current?.close();
      }
    };
  }, [isLoggedIn]);

  useEffect(() => {
    function updateVH() {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty("--vh", `${vh}px`);
    }

    updateVH();

    window.addEventListener("resize", updateVH);
    window.addEventListener("orientationchange", updateVH);

    return () => {
      window.removeEventListener("resize", updateVH);
      window.removeEventListener("orientationchange", updateVH);
    };
  }, []);

  const onSubmitHandler = (message?: string) => {
    const val =
      typeof message === "string" ? message.trim() : inputValue.trim();

    if (!val || !socketRef.current) return;

    const userMessage: Message = {
      sender: "user",
      message: val,
      accountId: user?.id,
      name: "",
      email: user?.email,
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    socketRef.current.send(JSON.stringify({message: val}));
    setInputValue("");
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({behavior: "smooth"});
  }, [messages]);

  return (
    <div className="root-container-blk">
      {!isLoggedIn && <LoginFormContainer />}
      {!isLoggedIn ? (
        <>
          <SiderBar isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
          <div className="iris-container"></div>
        </>
      ) : (
        <React.Fragment>
          <SiderBar isExpanded={isExpanded} setIsExpanded={setIsExpanded} />
          {!isConnected ? (
            <div className="iris-container" style={{justifyContent: "center"}}>
              <Spinner variant="large-spinner" alignSpin="center-spinner" />
            </div>
          ) : (
            <div className="iris-container">
              <div
                className={`iris-center-content ${
                  messages.length > 0 ? "messageNotEmpty" : "messageEmpty"
                }
          }`}>
                <h1
                  className={`iris-title ${messages.length ? "fade-out" : ""} ${
                    isExpanded ? "sidebar-expanded" : ""
                  }`}>
                  iRis - your agentic Amazon Connect support partner.
                </h1>
                <ChatHistory
                  messages={messages}
                  bottomRef={bottomRef}
                  isLoading={isLoading}
                  isThinking={isThinking}
                  setInputValue={setInputValue}
                  onSubmit={onSubmitHandler}
                />
              </div>
              <ChatInput
                messages={messages}
                inputValue={inputValue}
                setInputValue={setInputValue}
                onSubmit={onSubmitHandler}
                isExpanded={isExpanded}
              />
              <Footer />
              <div className="overflow-div-blk"></div>
            </div>
          )}
        </React.Fragment>
      )}
    </div>
  );
};

export default App;
